import { Observable, of, forkJoin } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import {
  AfiescaDocumentType,
  AprilSelectionPayload,
  ComparaisonService
} from '../../services/comparaison.service';
import { UnifiedProduct } from './comparaison.component';
import { normalizeAprilContributionType as normalizeAprilContributionTypeValue } from './april-products.util';

/**
 * Service de gestion des devis pour tous les assureurs
 * Gère le téléchargement et l'envoi par email des devis
 */
export class DevisService {
  
  constructor(
    private comparaisonService: ComparaisonService,
    private messageService: MessageService
  ) {}

  private buildLifesquareProductData(product: UnifiedProduct): any {
    const raw = product.data || {};

    return {
      ...raw,
      productCode: product.productCode || raw.product_code || raw.offer?.id || raw.offer_id || raw.id,
      productName: product.productName || raw.product_name || raw.offer?.name || raw.name,
      monthlyPremium: product.monthlyPremium ?? product.monthlyPrice ?? raw.monthly_premium ?? raw.monthlyPremium,
      totalCost: product.totalCost ?? product.firstYearPrice ?? raw.total_cost ?? raw.total_premium ?? raw.totalCost,
      cost8Years: product.cost8Years ?? product.eightYearsPrice ?? raw.cost_8_years ?? raw.cost8Years,
      yearlyAverageRate: product.yearlyAverageRate ?? raw.annual_average_rate ?? raw.yearlyAverageRate,
      taea: product.taea ?? raw.taea ?? raw.annual_average_rate ?? 0,
      borrowerPrices: raw.borrower_prices || raw.borrowerPrices || raw.raw_price?.borrowerPrices || [],
      quotations: raw.quotations || raw.raw_price?.quotations || [],
      offer: raw.offer || null,
    };
  }

  private buildZeniooProductData(product: UnifiedProduct): any {
    const raw = product.data || {};

    return {
      ...raw,
      productCode: product.productCode || raw.product_code,
      productName: product.productName || raw.product_name || product.productCode,
      monthlyPremium: product.monthlyPremium ?? product.monthlyPrice ?? raw.monthly_premium,
      totalCost: product.totalCost ?? product.firstYearPrice ?? raw.total_premium,
      cost8Years: product.cost8Years ?? product.eightYearsPrice ?? raw.premium_8_years,
      yearlyAverageRate: product.yearlyAverageRate ?? raw.yearlyAverageRate,
      taea: product.taea ?? raw.taea ?? 0,
      annualPremium: raw.annual_premium,
      fraisCourtage: raw.frais_courtage ?? product.fees ?? 0,
      contributionType: product.contributionType || raw.contribution_type || raw.contributionType,
      beneficiaires: raw.beneficiaires || [],
    };
  }

  /**
   * Télécharge le devis Zenioo
   */
  
  telechargerDevisLifesquare(
    product: UnifiedProduct, 
    sentData: any, 
    loadingCallback?: (loading: boolean) => void
  ): void {
    if (!sentData) {
      this.messageService.add({severity: 'error', summary: 'Données manquantes', detail: 'Veuillez d\'abord lancer une simulation avant de générer un devis LifeSquare.'});
      return;
    }
    if (!product.productCode && !product.data?.offer_id && !product.data?.id) {
      this.messageService.add({severity: 'error', summary: 'Erreur', detail: 'Code produit LifeSquare manquant.'});
      return;
    }

    if (loadingCallback) loadingCallback(true);

    const productCode = product.productCode || product.data?.offer_id || product.data?.id;
    const productData = this.buildLifesquareProductData(product);

    this.comparaisonService.genererDevisLifesquare(sentData, productCode, productData).subscribe({
      next: (blob: Blob) => {
        if (loadingCallback) loadingCallback(false);
        const timestamp = new Date().toISOString().split('T')[0];
        const nomClient = sentData?.nom?.toUpperCase() || 'CLIENT';
        const productName = product.productName?.replace(/\s+/g, '_') || productCode;
        
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `DEVIS_LIFESQUARE_${nomClient}_${productName}_${timestamp}.pdf`;
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(blobUrl);
        document.body.removeChild(link);

        this.messageService.add({
          severity: 'success',
          summary: 'Devis LifeSquare téléchargé',
          detail: `Le devis "${product.productName || productCode}" a été téléchargé avec succès.`,
          life: 5000
        });
      },
      error: (err: any) => {
        if (loadingCallback) loadingCallback(false);
        let errorMessage = 'Une erreur est survenue lors de la génération du devis LifeSquare.';
        if (err?.error?.detail) errorMessage = err.error.detail;
        else if (err?.error?.message) errorMessage = err.error.message;
        this.messageService.add({severity: 'error', summary: 'Erreur LifeSquare', detail: errorMessage, life: 7000});
      }
    });
  }


  telechargerDevisZenioo(
    product: UnifiedProduct, 
    sentData: any, 
    loadingCallback?: (loading: boolean) => void
  ): void {
    if (!sentData) {
      this.messageService.add({
        severity: 'error',
        summary: 'Données manquantes',
        detail: 'Veuillez d\'abord lancer une simulation avant de générer un devis Zenioo.'
      });
      return;
    }

    if (!product.productCode) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Code produit Zenioo manquant.'
      });
      return;
    }

    if (loadingCallback) loadingCallback(true);

    const productData = this.buildZeniooProductData(product);

    this.comparaisonService.genererDevisZenioo(
      sentData,
      product.productCode,
      productData
    ).subscribe({
      next: (blob: Blob) => {
        if (loadingCallback) loadingCallback(false);

        const timestamp = new Date().toISOString().split('T')[0];
        const nomClient = sentData?.nom?.toUpperCase() || 'CLIENT';
        const productName = product.productName?.replace(/\s+/g, '_') || product.productCode;

        // Créer un lien de téléchargement
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `DEVIS_ZENIOO_${nomClient}_${productName}_${timestamp}.pdf`;
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(blobUrl);
        document.body.removeChild(link);

        this.messageService.add({
          severity: 'success',
          summary: 'Devis Zenioo téléchargé',
          detail: `Le devis "${product.productName}" a été téléchargé avec succès.`,
          life: 5000
        });
      },
      error: (err) => {
        if (loadingCallback) loadingCallback(false);

        let errorMessage = 'Une erreur est survenue lors de la génération du devis Zenioo.';
        if (err?.error?.detail) {
          errorMessage = err.error.detail;
        } else if (err?.error?.message) {
          errorMessage = err.error.message;
        }

        this.messageService.add({
          severity: 'error',
          summary: 'Erreur Zenioo',
          detail: errorMessage,
          life: 7000
        });
      }
    });
  }

  /**
   * Envoie le devis Zenioo par email
   */
  envoyerDevisZeniooParEmail(
    product: UnifiedProduct, 
    sentData: any, 
    email: string,
    loadingCallback?: (loading: boolean) => void
  ): void {
    if (!sentData) {
      this.messageService.add({
        severity: 'error',
        summary: 'Données manquantes',
        detail: 'Veuillez d\'abord lancer une simulation.'
      });
      return;
    }

    if (!email) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Email manquant',
        detail: 'Veuillez renseigner l\'adresse email du client.'
      });
      return;
    }

    if (!product.productCode) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Code produit Zenioo manquant.'
      });
      return;
    }

    if (loadingCallback) loadingCallback(true);

    this.comparaisonService.envoyerDevisZeniooParEmail(
      sentData,
      product.productCode,
      email
    ).subscribe({
      next: (res) => {
        if (loadingCallback) loadingCallback(false);
        if (res.success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Email envoyé',
            detail: `Le devis Zenioo "${product.productName}" a été envoyé à ${email}.`,
            life: 5000
          });
        } else {
          this.messageService.add({
            severity: 'warn',
            summary: 'Envoi non confirmé',
            detail: 'L\'envoi de l\'email n\'a pas pu être confirmé.'
          });
        }
      },
      error: (err) => {
        if (loadingCallback) loadingCallback(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err?.error?.detail || 'Erreur lors de l\'envoi du devis par email.'
        });
      }
    });
  }

  /**
   * Ouvre le PDF April dans un nouvel onglet
   */
  openPdfApril(url: string): void {
    window.open(url, '_blank');
  }

  private normalizeAprilContributionType(value?: string | null): string | null {
    return normalizeAprilContributionTypeValue(value);
  }

  private buildAprilSelectionForProduct(
    product: UnifiedProduct,
    sentData: any
  ): AprilSelectionPayload | null {
    const explicitSelection = (product.aprilSelection ||
      product.data?.aprilSelection) as AprilSelectionPayload | undefined;
    if (explicitSelection?.mainBorrower) {
      return explicitSelection;
    }

    const productCode = product.data?.productCode || product.productCode;
    const contributionType = this.normalizeAprilContributionType(product.contributionType);

    if (!productCode || !contributionType) {
      return null;
    }

    const mainBorrower = { productCode, contributionType };
    const hasCoBorrower = !!(sentData?.EstCoEmprunteur && sentData?.coEmprunteurNom);

    return {
      mainBorrower,
      coBorrower: hasCoBorrower ? { ...mainBorrower } : undefined
    };
  }

  /**
   * Convertit un Blob en base64
   */
  private blobToBase64(blob: Blob): Observable<string> {
    return new Observable<string>((observer) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        const base64 = result && result.includes(',') ? result.split(',')[1] : result;
        observer.next(base64);
        observer.complete();
      };
      reader.onerror = (error) => {
        observer.error(error);
      };
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Construit une pièce jointe pour l'amortissement à partir d'un produit
   * Gère tous les types d'assureurs: ZENIOO, NEOLIANE, UTWIN, CARDIF, APRIL, AFI-ESCA
   */
  buildAmortissementAttachmentForProduct(
    product: UnifiedProduct,
    sentData: any,
    utwinRefDevis: string | null
  ): Observable<any | null> {
    const clientName = sentData?.nom?.toUpperCase() || 'CLIENT';
    const safeProductName = (product.productName || product.productCode || 'PRODUIT').replace(/\s+/g, '_');
    const dateStr = new Date().toISOString().split('T')[0];

    // ZENIOO -----------------------------------------------------------------
    
    if (product.type === 'lifesquare') {
      return this.buildLifesquareAttachment(product, sentData, clientName, safeProductName, dateStr);
    }
    
    if (product.type === 'zenioo') {
      return this.buildZeniooAttachment(product, sentData, clientName, safeProductName, dateStr);
    }

    // NEOLIANE ---------------------------------------------------------------
    if (product.type === 'neoliane') {
      return this.buildNeolianeAttachment(product, sentData, clientName, safeProductName, dateStr);
    }

    // UTWIN ------------------------------------------------------------------
    if (product.type === 'utwin') {
      return this.buildUtwinAttachment(product, sentData, utwinRefDevis, clientName, dateStr);
    }

    // CARDIF -----------------------------------------------------------------
    if (product.type === 'cardif') {
      return this.buildCardifAttachment(product, sentData, clientName, safeProductName, dateStr);
    }

    // APRIL ------------------------------------------------------------------
    if (product.type === 'april') {
      return this.buildAprilAttachment(product, sentData, clientName, safeProductName, dateStr);
    }

    // AFI-ESCA ---------------------------------------------------------------
    if (product.type === 'afiesca') {
      return this.buildAfiescaAttachment(product, sentData, clientName, safeProductName, dateStr);
    }

    return of(null);
  }

  /**
   * Construit l'attachement pour un devis ZENIOO
   */
  
  private buildLifesquareAttachment(
    product: UnifiedProduct,
    sentData: any,
    clientName: string,
    safeProductName: string,
    dateStr: string
  ): Observable<any | null> {
    const productCode = product.productCode || product.data?.offer_id || product.data?.id;
    if (!sentData || !productCode) {
      return of(null);
    }
    const productData = this.buildLifesquareProductData(product);

    return this.comparaisonService.genererDevisLifesquare(sentData, productCode, productData).pipe(
      switchMap((blob: Blob) =>
        this.blobToBase64(blob).pipe(
          map((base64: string) => ({
            filename: `DEVIS_LIFESQUARE_${clientName}_${safeProductName}_${dateStr}.pdf`,
            content_type: 'application/pdf',
            data_base64: base64
          }))
        )
      ),
      catchError(() => of(null))
    );
  }


  private buildZeniooAttachment(
    product: UnifiedProduct,
    sentData: any,
    clientName: string,
    safeProductName: string,
    dateStr: string
  ): Observable<any | null> {
    if (!sentData || !product.productCode) {
      return of(null);
    }

    const productData = this.buildZeniooProductData(product);

    return this.comparaisonService.genererDevisZenioo(
      sentData,
      product.productCode,
      productData
    ).pipe(
      switchMap((blob: Blob) =>
        this.blobToBase64(blob).pipe(
          map((base64: string) => ({
            filename: `DEVIS_ZENIOO_${clientName}_${safeProductName}_${dateStr}.pdf`,
            content_type: 'application/pdf',
            data_base64: base64
          }))
        )
      ),
      catchError(() => of(null))
    );
  }

  /**
   * Construit l'attachement pour un devis NEOLIANE
   */
  private buildNeolianeAttachment(
    product: UnifiedProduct,
    sentData: any,
    clientName: string,
    safeProductName: string,
    dateStr: string
  ): Observable<any | null> {
    if (!sentData || !product.data?.product_id) {
      return of(null);
    }

    // Préparer les données de tarification pour éviter une re-tarification
    const productData = {
      monthlyPremium: product.monthlyPrice || 0,
      totalCost: product.firstYearPrice || 0,
      cost8Years: product.eightYearsPrice || 0,
      taea: product.data?.taea || 0
    };

    return this.comparaisonService.genererDevisNeoliane(
      sentData,
      product.data.product_id,
      productData
    ).pipe(
      switchMap((blob: Blob) =>
        this.blobToBase64(blob).pipe(
          map((base64: string) => ({
            filename: `DEVIS_NEOLIANE_${clientName}_${safeProductName}_${dateStr}.pdf`,
            content_type: 'application/pdf',
            data_base64: base64
          }))
        )
      ),
      catchError(() => of(null))
    );
  }

  /**
   * Construit l'attachement pour un devis UTWIN
   */
  private buildUtwinAttachment(
    product: UnifiedProduct,
    sentData: any,
    utwinRefDevis: string | null,
    clientName: string,
    dateStr: string
  ): Observable<any | null> {
    const refDevis = product.data?.RefSimu || utwinRefDevis;

    if (!sentData || !refDevis) {
      return of(null);
    }

    return this.comparaisonService.downloadUtwinPdf(
      refDevis,
      sentData
    ).pipe(
      switchMap((blob: Blob) =>
        this.blobToBase64(blob).pipe(
          map((base64: string) => ({
            filename: `DEVIS_UTWIN_${clientName}_${refDevis}_${dateStr}.pdf`,
            content_type: 'application/pdf',
            data_base64: base64
          }))
        )
      ),
      catchError(() => of(null))
    );
  }

  /**
   * Construit l'attachement pour un devis CARDIF
   */
  private buildCardifAttachment(
    product: UnifiedProduct,
    sentData: any,
    clientName: string,
    safeProductName: string,
    dateStr: string
  ): Observable<any | null> {
    if (!sentData || !product.data?.codeProduit) {
      return of(null);
    }

    const payload = {
      ...sentData,
      selectedProductCode: product.data.codeProduit
    };

    return this.comparaisonService.genererDevisCardif(payload).pipe(
      switchMap((blob: Blob) =>
        this.blobToBase64(blob).pipe(
          map((base64: string) => ({
            filename: `DEVIS_CARDIF_${clientName}_${safeProductName}_${dateStr}.pdf`,
            content_type: 'application/pdf',
            data_base64: base64
          }))
        )
      ),
      catchError(() => of(null))
    );
  }

  /**
   * Construit l'attachement pour un devis APRIL
   */
  private buildAprilAttachment(
    product: UnifiedProduct,
    sentData: any,
    clientName: string,
    safeProductName: string,
    dateStr: string
  ): Observable<any | null> {
    if (!sentData) {
      return of(null);
    }

    const aprilSelection = this.buildAprilSelectionForProduct(product, sentData);
    const productCode = product.data?.productCode || product.productCode;
    const contributionType = this.normalizeAprilContributionType(product.contributionType) || undefined;

    return this.comparaisonService.genererPdfApril(
      sentData,
      productCode,
      contributionType,
      aprilSelection || undefined
    ).pipe(
      switchMap((res: any) => {
        if (!res || !res.success || !Array.isArray(res.documents) || res.documents.length === 0) {
          return of(null);
        }

        const estimationDocs = res.documents.filter((doc: any) => {
          const title = (doc.documentTitle || '').toString().toLowerCase();
          const location = (doc.documentLocation || '').toString().toLowerCase();
          return title.startsWith('estimation_tarifaire') || location.includes('estimation_tarifaire');
        });

        const doc = estimationDocs[0] || res.documents[0];
        if (!doc || !doc.documentLocation) {
          return of(null);
        }

        return this.comparaisonService.downloadAprilPdfViaBackend(doc.documentLocation).pipe(
          switchMap((blob: Blob) =>
            this.blobToBase64(blob).pipe(
              map((base64: string) => ({
                filename: `DEVIS_APRIL_${clientName}_${safeProductName}_${dateStr}.pdf`,
                content_type: 'application/pdf',
                data_base64: base64
              }))
            )
          )
        );
      }),
      catchError(() => of(null))
    );
  }

  /**
   * Construit l'attachement pour un devis AFI-ESCA
   */
  private buildAfiescaAttachment(
    product: UnifiedProduct,
    sentData: any,
    clientName: string,
    safeProductName: string,
    dateStr: string
  ): Observable<any | null> {
    if (!sentData) {
      return of(null);
    }

    return this.comparaisonService.genererDevisAfiesca(sentData).pipe(
      switchMap((response: any) => {
        if (!response || !response.success || !response.numero_dossier) {
          return of(null);
        }

        return this.comparaisonService.telechargerDocumentAfiesca(
          response.numero_dossier,
          'PROJET_TARIFICATION' as AfiescaDocumentType
        ).pipe(
          switchMap((blob: Blob) =>
            this.blobToBase64(blob).pipe(
              map((base64: string) => ({
                filename: `DEVIS_AFIESCA_${clientName}_${safeProductName}_${dateStr}.pdf`,
                content_type: 'application/pdf',
                data_base64: base64
              }))
            )
          )
        );
      }),
      catchError(() => of(null))
    );
  }

  /**
   * Construit les attachements pour tous les produits sélectionnés
   */
  buildAllAttachments(
    selectedProducts: UnifiedProduct[],
    sentData: any,
    utwinRefDevis: string | null
  ): Observable<any[]> {
    if (!selectedProducts.length) {
      return of([]);
    }

    const attachmentObservables = selectedProducts.map(product =>
      this.buildAmortissementAttachmentForProduct(product, sentData, utwinRefDevis)
    );

    return forkJoin(attachmentObservables).pipe(
      map(attachments => (attachments || []).filter(att => !!att))
    );
  }
}
